// ─── DRAFT PERSISTENCE ──────────────────────────────────────────────────────
// Saves message drafts to localStorage so they survive app close/reopen.
// Keyed by context (conversation ID, channel ID, etc.)
//
// Internally **debounced** — saveDraft() is called on every keystroke from
// the RP/DM/Channel inputs, but the actual localStorage.setItem only fires
// after typing pauses for DRAFT_DEBOUNCE_MS. localStorage.setItem on iOS
// Safari can take 5-30ms per call, which on a 60Hz device means dropped
// frames per keystroke — i.e. typing lag. Debouncing kills that I/O.
//
// Reads still see the latest typed value (we maintain an in-memory mirror).
// Page-unload / visibility-hidden triggers a flush so nothing is lost.
//
// Usage (unchanged):
//   saveDraft('dm', convoId, text);
//   const text = loadDraft('dm', convoId);
//   clearDraft('dm', convoId);
//
// ─── SYNCED BETWEEN DEVICES (v425) ──────────────────────────────────────────
// Haley, 2026-10-22: "I would like to be able to see my response I drafted in
// both the app and the desktop if I switch from device to device." Every
// saveDraft also lands in public.composer_drafts (one row per person per
// thread, owner-only RLS) after a 1.2 s pause, and every clearDraft deletes
// it. One realtime channel per signed-in session streams the owner's rows
// from their OTHER devices; a composer subscribes through useSyncedDraft and
// applies a remote draft only when its box is empty, unchanged since the last
// sync, or older than the remote by more than two seconds — a writer typing
// on THIS device is never overwritten. Offline, the local copy stands and the
// next keystroke retries.
import { supabase } from './supabaseClient.js';

const DRAFT_PREFIX = 'sixguys_draft_';
// v425 (Haley, 2026-10-22: "drafts in specific channels should be saved
// differently so responses can't be like erased by other drafts"): a draft is
// a response in progress and is keyed per thread. Nothing but the writer's own
// send or delete may remove it — the 7-day expiry and the 50-draft eviction
// are gone. The only cleanup left removes corrupt entries, and the safety cap
// below is far above anything a person reaches.
const MAX_DRAFTS = 2000;
const DRAFT_DEBOUNCE_MS = 350; // Tuned: long enough to coalesce typing bursts,
                               // short enough that a quick close still saves.

// Per-key debounce timers + the latest text waiting to be written.
// loadDraft() consults pendingValues first so an in-flight draft is visible
// to readers (e.g., when a sibling component remounts).
const pendingTimers = new Map(); // key → timeout id
const pendingValues = new Map(); // key → latest text

function flushKey(key) {
  if (!pendingTimers.has(key)) return;
  clearTimeout(pendingTimers.get(key));
  pendingTimers.delete(key);
  if (!pendingValues.has(key)) return;
  const text = pendingValues.get(key);
  pendingValues.delete(key);
  try {
    if (!text || text.trim() === '') {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify({ text, savedAt: Date.now() }));
    }
  } catch {
    // Storage full or unavailable — silently fail
  }
}

/**
 * Force any pending debounced writes to localStorage immediately.
 * Called automatically on page unload / visibility hidden, but also exported
 * for callers that want explicit control (e.g., before navigation).
 */
export function flushAllDrafts() {
  for (const key of Array.from(pendingTimers.keys())) flushKey(key);
}

// Auto-flush on page unload / tab hide. Without this, fast typing then a
// fast close would lose the last 350ms of typing.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushAllDrafts);
  window.addEventListener('beforeunload', flushAllDrafts);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAllDrafts();
  });
}

// ─── SERVER SYNC ─────────────────────────────────────────────────────────────
const SERVER_DEBOUNCE_MS = 1200;          // a pause this long sends the draft to the server
const MAX_DRAFT_CHARS = 20000;
const LOCAL_EDIT_GRACE_MS = 2000;         // a remote write younger than our own last keystroke by less than this loses
const SERVER_SCOPES = new Set(['channel', 'dm', 'rp', 'group', 'user_dm']);
const DEVICE_ID = (() => {
  try {
    let d = sessionStorage.getItem('mfml_draft_device');
    if (!d) { d = Math.random().toString(36).slice(2, 10); sessionStorage.setItem('mfml_draft_device', d); }
    return d;
  } catch { return Math.random().toString(36).slice(2, 10); }
})();

let draftUserId = null;
let accessToken = null;
let syncChannel = null;
const serverTimers = new Map();   // key → timeout id
const serverPending = new Map();  // key → { type, id, text }
const lastSynced = new Map();     // key → text last known to match the server
const lastLocalEdit = new Map();  // key → ms of our last keystroke on this device
const listeners = new Map();      // key → Set<cb>
const anyListeners = new Set();   // "the set of threads with drafts changed" subscribers (sidebar marks)
const serverKnown = new Set();    // keys the server reports as holding text
const keyOf = (type, id) => `${DRAFT_PREFIX}${type}_${id}`;

/** The rule a composer uses before replacing its text with a remote draft. Pure, tested. */
export function shouldApplyRemote(current, synced, remoteText, { remoteAt = null, localEditAt = null, graceMs = LOCAL_EDIT_GRACE_MS } = {}) {
  const cur = current || '';
  const remote = remoteText || '';
  if (remote === cur) return false;
  if (cur === '' || cur === (synced ?? undefined)) return true;
  const rAt = remoteAt ? Date.parse(remoteAt) : NaN;
  if (Number.isFinite(rAt) && Number.isFinite(localEditAt) && rAt > localEditAt + graceMs) return true;
  return false;
}

async function refreshToken() {
  try { const { data } = await supabase.auth.getSession(); accessToken = data?.session?.access_token || null; } catch { /* keep the old one */ }
}

async function pushServer(key) {
  if (serverTimers.has(key)) { clearTimeout(serverTimers.get(key)); serverTimers.delete(key); }
  const p = serverPending.get(key);
  if (!p) return;
  serverPending.delete(key);
  if (!draftUserId) return;
  if (lastSynced.get(key) === p.text) return;
  try {
    if (!p.text || !p.text.trim()) {
      await supabase.from('composer_drafts').delete().eq('user_id', draftUserId).eq('scope', p.type).eq('scope_id', String(p.id));
      lastSynced.set(key, '');
    } else {
      const { error } = await supabase.from('composer_drafts').upsert(
        { user_id: draftUserId, scope: p.type, scope_id: String(p.id), text: p.text.slice(0, MAX_DRAFT_CHARS), device: DEVICE_ID },
        { onConflict: 'user_id,scope,scope_id' });
      if (!error) { lastSynced.set(key, p.text); serverKnown.add(key); }
    }
  } catch { /* offline — the local copy stands; the next keystroke retries */ }
}

function scheduleServer(type, id, text) {
  if (!draftUserId || !SERVER_SCOPES.has(type)) return;
  const key = keyOf(type, id);
  serverPending.set(key, { type, id, text });
  if (serverTimers.has(key)) clearTimeout(serverTimers.get(key));
  serverTimers.set(key, setTimeout(() => pushServer(key), SERVER_DEBOUNCE_MS));
}

/** pagehide / hidden: the browser may never run a promise again, so the
 *  pending drafts go out as keepalive requests with the cached token. */
function flushServerNow() {
  if (!draftUserId || !accessToken) return;
  const base = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!base || !anon || typeof fetch !== 'function') return;
  for (const [key, p] of Array.from(serverPending.entries())) {
    serverPending.delete(key);
    if (serverTimers.has(key)) { clearTimeout(serverTimers.get(key)); serverTimers.delete(key); }
    if (lastSynced.get(key) === p.text) continue;
    const headers = { apikey: anon, Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
    try {
      if (!p.text || !p.text.trim()) {
        fetch(`${base}/rest/v1/composer_drafts?user_id=eq.${encodeURIComponent(draftUserId)}&scope=eq.${encodeURIComponent(p.type)}&scope_id=eq.${encodeURIComponent(String(p.id))}`, { method: 'DELETE', headers, keepalive: true }).catch(() => {});
      } else {
        fetch(`${base}/rest/v1/composer_drafts?on_conflict=user_id,scope,scope_id`, {
          method: 'POST', keepalive: true,
          headers: { ...headers, Prefer: 'resolution=merge-duplicates,return=minimal' },
          body: JSON.stringify({ user_id: draftUserId, scope: p.type, scope_id: String(p.id), text: p.text.slice(0, MAX_DRAFT_CHARS), device: DEVICE_ID }),
        }).catch(() => {});
      }
      lastSynced.set(key, p.text);
    } catch { /* best effort */ }
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushServerNow);
  window.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushServerNow(); });
}

function notify(key, remote) {
  const set = listeners.get(key);
  if (set) for (const cb of Array.from(set)) { try { cb(remote); } catch { /* a listener must never break the stream */ } }
  notifyAny();
}

// ─── DRAFT MARKS: which threads hold an unsent draft (sidebar ✎) ─────────────
function notifyAny() { for (const cb of Array.from(anyListeners)) { try { cb(); } catch { /* never breaks the stream */ } } }
/** Subscribe to "the set of threads with drafts changed". Returns the unsubscribe. */
export function onAnyDraftChange(cb) {
  if (typeof cb !== 'function') return () => {};
  anyListeners.add(cb);
  return () => anyListeners.delete(cb);
}
/** Ids of this person's threads in `scope` that hold a non-empty draft, from local storage, the in-memory mirror and the server. */
export function draftIdsFor(scope) {
  const prefix = `${DRAFT_PREFIX}${scope}_`;
  const ids = new Set();
  const consider = (key, text) => { if (key.startsWith(prefix)) { if (text && String(text).trim()) ids.add(key.slice(prefix.length)); else ids.delete(key.slice(prefix.length)); } };
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      try { consider(key, JSON.parse(localStorage.getItem(key))?.text); } catch { /* corrupt */ }
    }
  } catch { /* storage unavailable */ }
  for (const key of serverKnown) if (key.startsWith(prefix)) ids.add(key.slice(prefix.length));
  for (const [key, text] of pendingValues) consider(key, text);   // the newest keystrokes win
  return ids;
}
async function loadServerKnown() {
  if (!draftUserId) return;
  try {
    const { data } = await supabase.from('composer_drafts').select('scope, scope_id').eq('user_id', draftUserId).limit(1000);
    serverKnown.clear();
    for (const r of data || []) serverKnown.add(keyOf(r.scope, r.scope_id));
    notifyAny();
  } catch { /* offline: local marks only */ }
}

function startDraftSync() {
  if (!draftUserId || syncChannel) return;
  const ch = supabase.channel(`drafts-${draftUserId}`);
  ch.on('postgres_changes', { event: '*', schema: 'public', table: 'composer_drafts', filter: `user_id=eq.${draftUserId}` }, (payload) => {
    const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
    if (!row?.scope || !row?.scope_id) return;
    if (payload.eventType !== 'DELETE' && row.device === DEVICE_ID) return; // our own echo
    const key = keyOf(row.scope, row.scope_id);
    const text = payload.eventType === 'DELETE' ? '' : String(row.text || '');
    if (text.trim()) serverKnown.add(key); else serverKnown.delete(key);
    notify(key, { text, updatedAt: row.updated_at || new Date().toISOString(), remote: true });
  }).subscribe();
  syncChannel = ch;
}
function stopDraftSync() {
  if (syncChannel) { try { supabase.removeChannel(syncChannel); } catch { /* gone */ } syncChannel = null; }
}

/** Called by the app when the signed-in user changes (null on sign-out). */
export function setDraftUser(userId) {
  const next = userId || null;
  if (next === draftUserId) return;
  stopDraftSync();
  draftUserId = next;
  lastSynced.clear();
  serverKnown.clear();
  if (draftUserId) { refreshToken(); startDraftSync(); loadServerKnown(); }
  notifyAny();
}

/** This person's draft for a thread as the server has it, or null. */
export async function fetchServerDraft(type, contextId) {
  if (!draftUserId || !contextId || !SERVER_SCOPES.has(type)) return null;
  try {
    const { data, error } = await supabase.from('composer_drafts').select('text, updated_at, device')
      .eq('user_id', draftUserId).eq('scope', type).eq('scope_id', String(contextId)).maybeSingle();
    if (error || !data) return null;
    return { text: String(data.text || ''), updatedAt: data.updated_at, device: data.device, remote: data.device !== DEVICE_ID };
  } catch { return null; }
}

/** Subscribe to remote changes of one thread's draft. Returns the unsubscribe. */
export function onDraftChange(type, contextId, cb) {
  if (!contextId || typeof cb !== 'function') return () => {};
  const key = keyOf(type, contextId);
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(cb);
  return () => { const set = listeners.get(key); if (set) { set.delete(cb); if (!set.size) listeners.delete(key); } };
}

/** Write a draft that CAME FROM the server: local copy only, and remembered as synced. */
export function setDraftLocal(type, contextId, text) {
  if (!contextId) return;
  const key = keyOf(type, contextId);
  if (pendingTimers.has(key)) { clearTimeout(pendingTimers.get(key)); pendingTimers.delete(key); }
  pendingValues.delete(key);
  if (serverTimers.has(key)) { clearTimeout(serverTimers.get(key)); serverTimers.delete(key); }
  serverPending.delete(key);
  try {
    if (!text || !text.trim()) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify({ text, savedAt: Date.now() }));
  } catch { /* storage unavailable */ }
  lastSynced.set(key, text || '');
  if (text && text.trim()) serverKnown.add(key); else serverKnown.delete(key);
  notifyAny();
}

export function lastSyncedText(type, contextId) { return lastSynced.get(keyOf(type, contextId)); }
export function lastLocalEditAt(type, contextId) {
  const key = keyOf(type, contextId);
  if (lastLocalEdit.has(key)) return lastLocalEdit.get(key);
  try { const stored = localStorage.getItem(key); if (stored) return Number(JSON.parse(stored).savedAt) || null; } catch { /* ignore */ }
  return null;
}


/**
 * Save a draft message for a specific context. Debounced — see file header.
 * @param {'dm'|'channel'|'rp'|'group'|'user_dm'} type - The type of input
 * @param {string} contextId - Conversation/channel/scene ID
 * @param {string} text - The draft text to save
 */
export function saveDraft(type, contextId, text) {
  if (!contextId) return;
  const key = `${DRAFT_PREFIX}${type}_${contextId}`;

  // Always update the in-memory mirror so loadDraft sees the latest value
  // even before the localStorage write fires.
  pendingValues.set(key, text);
  lastLocalEdit.set(key, Date.now());

  // Reset the debounce timer for this key.
  if (pendingTimers.has(key)) clearTimeout(pendingTimers.get(key));
  pendingTimers.set(key, setTimeout(() => flushKey(key), DRAFT_DEBOUNCE_MS));

  // v425: and, after a longer pause, the server copy the other device reads.
  scheduleServer(type, contextId, text);
  notifyAny();
}

/**
 * Load a draft message for a specific context.
 * Checks the in-memory mirror first (for in-flight unwritten drafts),
 * then falls back to localStorage.
 * @param {'dm'|'channel'|'rp'|'user_dm'} type
 * @param {string} contextId
 * @returns {string} The draft text, or empty string if none
 */
export function loadDraft(type, contextId) {
  if (!contextId) return '';
  const key = `${DRAFT_PREFIX}${type}_${contextId}`;
  if (pendingValues.has(key)) return pendingValues.get(key) || '';
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return '';
    const parsed = JSON.parse(stored);
    // v425: no expiry — a draft lives until it is sent or deleted
    return parsed.text || '';
  } catch {
    return '';
  }
}

/**
 * Clear a draft after successful send. Cancels any pending debounced write
 * and removes from both the in-memory mirror and localStorage.
 * @param {'dm'|'channel'|'rp'|'user_dm'} type
 * @param {string} contextId
 */
export function clearDraft(type, contextId) {
  if (!contextId) return;
  const key = `${DRAFT_PREFIX}${type}_${contextId}`;
  if (pendingTimers.has(key)) {
    clearTimeout(pendingTimers.get(key));
    pendingTimers.delete(key);
  }
  pendingValues.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {}
  // v425: the other device must stop showing a draft that was just sent
  if (serverTimers.has(key)) { clearTimeout(serverTimers.get(key)); serverTimers.delete(key); }
  serverPending.delete(key);
  lastLocalEdit.set(key, Date.now());
  serverKnown.delete(key);
  if (draftUserId && SERVER_SCOPES.has(type) && lastSynced.get(key) !== '') {
    lastSynced.set(key, '');
    supabase.from('composer_drafts').delete().eq('user_id', draftUserId).eq('scope', type).eq('scope_id', String(contextId)).then(() => {}, () => {});
  }
  notifyAny();
}

/**
 * Remove corrupt draft entries. v425: nothing time-based any more — a draft
 * is never removed because it is old or because other drafts exist.
 * Call occasionally (e.g., on app load).
 */
export function cleanupOldDrafts() {
  try {
    const draftKeys = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) {
        draftKeys.push(key);
      }
    }

    // Remove corrupt entries only
    for (const key of draftKeys) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) JSON.parse(stored);
      } catch {
        localStorage.removeItem(key); // corrupt data — remove
      }
    }

    // If still too many drafts, remove oldest
    const remaining = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          remaining.push({ key, savedAt: parsed.savedAt || 0 });
        } catch {
          remaining.push({ key, savedAt: 0 });
        }
      }
    }

    if (remaining.length > MAX_DRAFTS) {
      remaining.sort((a, b) => a.savedAt - b.savedAt);
      const toRemove = remaining.slice(0, remaining.length - MAX_DRAFTS);
      toRemove.forEach(d => localStorage.removeItem(d.key));
    }
  } catch {}
}

export default { saveDraft, loadDraft, clearDraft, cleanupOldDrafts, flushAllDrafts, setDraftUser, fetchServerDraft, onDraftChange, setDraftLocal, shouldApplyRemote, onAnyDraftChange, draftIdsFor };
